<template>
  <ElevationPage
    v-model:selectedLocation="selectedLocation"
    :currentLocationSupported="true"
    :location="location"
    :error="error"
    :loading="loading"
    :elevationStatus="elevationStatus"
  >
    <template #settings-bottom>
      <!-- dimmed rather than removed without location access, so the server-rendered layout doesn't shift -->
      <Toggle
        :options="readingSourceOptions"
        v-model="useWeb"
        key="readingSource"
        :inert="locationUnavailable"
        :class="{ 'opacity-50': locationUnavailable }"
      />
      <div flex flex-wrap gap-6>
        <NuxtLink
          class="text-blue-600 underline font-mono text-lg inline-flex gap-1 items-center hover:text-blue-400"
          to="/map"
        >
          <div class="i-material-symbols-location-on-rounded inline-block"></div>
          View on map
        </NuxtLink>
        <NuxtLink
          class="text-green-600 hover:text-green-400 underline font-mono text-lg inline-flex gap-1 items-center"
          to="/peaks"
        >
          <div class="i-icon-park-solid-three-triangles inline-block"></div>
          Nearby peaks
        </NuxtLink>
      </div>
    </template>
  </ElevationPage>
</template>
<script setup>
import { useDocumentVisibility } from "@vueuse/core";

useHead({
  bodyAttrs: {
    class: "home",
  },
});
const page = await useContent("/elevation");

useServerSeoMeta({
  title: page.value.title,
  description: page.value.description,
  ogDescription: page.value.description,
});

defineOgImage({
  title: page.value.title,
  subtitle: "Find the elevation of your current location, or any point on Earth.",
});

const {
  updateLocation,
  useWeb,
  isLocationSupported,
  locationPermissionStatus,
  location,
  error,
  loading,
  elevationStatus,
} = useLocation();

const selectedLocation = ref(
  !isLocationSupported.value
    ? null
    : {
        name: "Current Location",
        module: "currentLocation",
      }
);

watch(selectedLocation, async (selectedLocation) => {
  if (selectedLocation?.type == "currentLocation") {
    await updateLocation();
    if (!isLocationSupported.value) {
      selectedLocation.value = null;
      enableLocationDisabledDialog();
    }
  } else if (selectedLocation?.location) {
    const path = {
      name: "location-latitude,longitude-name-wiki",
      params: {
        latitude: round(selectedLocation.location.latitude, 5),
        longitude: round(selectedLocation.location.longitude, 5),
        name: encodeUrlTitle(selectedLocation.location?.name),
      },
    };
    await navigateTo(path);
  }
});

watchEffect(() => {
  // an error occurred getting the user's location, so clear the selected location
  if (isGeolocationError(error) && !isNumber(location.value?.latitude)) {
    selectedLocation.value = null;
  }
  if (isNumber(location.value?.latitude)) {
    selectedLocation.value = {
      name: "Current Location",
      module: "currentLocation",
    };
  }
});

updateLocation();
// the user allowed location access again from LocationPermissionButton
onLocationEnabled(() => updateLocation());

// the web/device toggle means nothing without location access; `error` stays set while each new request is pending
const locationUnavailable = computed(
  () =>
    !isLocationSupported.value ||
    locationPermissionStatus.value === "denied" ||
    (isGeolocationError(error) && error.value.code === 1),
);

const visibility = useDocumentVisibility();

watch(visibility, (visibility) => {
  if (visibility === "visible") {
    updateLocation();
  }
});

const readingSourceOptions = [
  {
    value: true,
    label: "Lookup on web",
    description: "Lookup on web is more accurate, but will not update in realtime as you move.",
  },
  {
    value: false,
    label: "Use device",
    description: "Use device can be less accurate, but will update in realtime as you move.",
  },
];
</script>
